import {useMutation, UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {
    httpUpdateFreelancer,
    HttpUpdateFreelancerOptionsAvailabilityPayload
} from '@features/settings/http/settings.http.ts'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {PhpUser, User} from '@features/user/types.ts'
import {captureException} from '@sentry/react'
import {errorHandler} from '@utilities/helpers.ts'
import {QUERY_KEYS} from '@/queryClient.ts'
import {z} from 'zod'

const ResponseShape = PhpUser.pick({shoot_available: true})
type ResponseShape = z.infer<typeof ResponseShape>

export const useChangeFreelancerAvailability = ({
    userId,
    options
}: {
    userId: number
    options?: Omit<
        UseMutationOptions<ResponseShape, unknown, HttpUpdateFreelancerOptionsAvailabilityPayload>,
        'mutationFn'
    >
}) => {
    const queryClient = useQueryClient()
    return useMutation<ResponseShape, unknown, HttpUpdateFreelancerOptionsAvailabilityPayload>({
        ...options,
        mutationFn: payload =>
            parseAxiosPromise({
                axiosPromise: httpUpdateFreelancer({payload, urlParams: {userId}}),
                onZodError: captureException,
                responseShape: ResponseShape
            }),
        onMutate: async variables => {
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.ME]})
            queryClient.setQueryData(
                [QUERY_KEYS.ME],
                (data: User): User => ({
                    ...data,
                    shootAvailable: variables.shoot_available
                })
            )
        },
        onError: async (error, variables, context) => {
            errorHandler(error)
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.ME]})
            queryClient.setQueryData(
                [QUERY_KEYS.ME],
                (data: User): User => ({
                    ...data,
                    shootAvailable: !data.shootAvailable
                })
            )
            options?.onError?.(error, variables, context)
        }
    })
}
